import { Button } from '@/components/ui/button'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { X } from 'lucide-react'
import { ReactNode } from 'react'

interface RecordPanelProps {
	open: boolean
	onClose: () => void
	title: string
	children: ReactNode
	footer?: ReactNode
	onSave?: () => void
	onDelete?: () => void
	saveLabel?: string
	deleteLabel?: string
	isSaving?: boolean
	isDeleting?: boolean
	showDelete?: boolean
	fullWidthSave?: boolean
	closeTooltip?: string
	isInboxModalOpen?: boolean
	onCloseAll?: () => void
}

export function RecordPanel({
	open,
	onClose,
	title,
	children,
	footer,
	onSave,
	onDelete,
	saveLabel = 'Save',
	deleteLabel = 'Delete',
	isSaving = false,
	isDeleting = false,
	showDelete = false,
	fullWidthSave = false,
	closeTooltip = 'Close',
	isInboxModalOpen = false,
	onCloseAll,
}: RecordPanelProps) {
	const handleOpenChange = (open: boolean) => {
		console.log('📋 RecordPanel: onOpenChange called with:', {
			open,
			isInboxModalOpen,
			stackTrace: new Error().stack
		})
		if (!open && !isInboxModalOpen) {
			console.log('📋 RecordPanel: Closing panel due to onOpenChange (inbox modal not open)')
			onClose()
		} else if (!open && isInboxModalOpen) {
			console.log('📋 RecordPanel: Ignoring close request because inbox modal is open')
		}
	}

	return (
		<Sheet open={open} onOpenChange={handleOpenChange} modal={false}>
			{/* Custom overlay for visual design when modal={false */}
			{open && (
				<div
					className="fixed inset-0 z-40 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
					onClick={() => {
						// Only close if inbox modal is not open
						if (!isInboxModalOpen) {
							onClose()
						}
					}}
				/>
			)}
			<SheetContent 
				side="right" 
				className="max-w-none w-[50vw] min-w-[600px] flex flex-col p-6 [&>button]:hidden"
			>
				<div className="flex-shrink-0 flex items-center justify-between mb-6">
					<h1 className="text-lg font-semibold">{title}</h1>
					<Button 
						variant="ghost" 
						size="icon"
						onClick={() => {
							// If both panels are open, close both; otherwise just close this panel
							if (isInboxModalOpen && onCloseAll) {
								onCloseAll()
							} else {
								onClose()
							}
						}}
						className="hover:bg-primary hover:text-primary-foreground"
						title={isInboxModalOpen ? "Close all panels" : closeTooltip}
					>
						<X className="h-4 w-4" />
					</Button>
				</div>

				<div className="flex-1 overflow-auto">
					{children}
				</div>

				{(footer || onSave || showDelete) && (
					<div className="flex-shrink-0 mt-6">
						{footer || (
							<div className="w-full space-y-3">
								{showDelete && onDelete && (
									<Button
										variant="destructive"
										onClick={onDelete}
										disabled={isDeleting || isSaving}
										className="w-full"
									>
										{isDeleting ? 'Deleting...' : deleteLabel}
									</Button>
								)}
								{onSave && (
									<Button 
										onClick={onSave} 
										disabled={isSaving}
										className={fullWidthSave ? 'w-full' : ''}
									>
										{isSaving ? 'Saving...' : saveLabel}
									</Button>
								)}
							</div>
						)}
					</div>
				)}
			</SheetContent>
		</Sheet>
	)
}
